import { createSelector } from 'reselect';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import localStorage from '@utils/localStorage';
import createKeyByUserName from '@utils/tempDashboard/createKeyByUserName';
import { setFromToByScrollingMark } from '@utils/scrollingWindowFromTo';
import { tryParse } from '@utils/tryParse';
import { getUseCaseLimitation } from '@src/components/common/Dashboard/UseCases/limitation';
import defaultTempDashboard from './tempDashboardsStore';
import {
  getActiveTabId,
  selectedCustomerIdSelector,
  selectedUserNameSelector,
} from '../common/selectors';
import { SCOPE, prefix } from './types';
import { isAppModule } from '@src/utils/constants';
import {
  isPresetDashboard,
  isSavedDashboard,
  isSharingDashboard,
  isDrillDownDashboard,
  getSharingIdFromUrl,
  getSavedDashboardIdFromUrl,
  getPresetKeyFromUrl,
} from '@src/utils/preset';

const selector = (state) => get(state, SCOPE);

export default selector;

export const afterInitializationSelector = createSelector(
  selector,
  (store) => store.afterInitialization,
);

export const currentActiveDashboard = createSelector(
  selector,
  (store) => store.currentActiveDashboard,
);

export const tempKeysSelector = createSelector(selector, (store) =>
  Array.from(store.keys).map((key) => `${key}`),
);

export const savedKeysSelector = createSelector(selector, (store) =>
  Array.from(store.savedRecentlyKeys).map((key) => `${key}`),
);

// sd-note: this gives the list of tabs that show up as pulse tabs for saved and temp dashboards
// recently saved dashboards are sorted here
export const dashboardListSelector = createSelector(
  tempKeysSelector,
  savedKeysSelector,
  selectedCustomerIdSelector,
  selectedUserNameSelector,
  (tempKeys, savedKeys, customerId, userName) => {
    const localStorageKey = createKeyByUserName(prefix, customerId, userName);
    let tempDashboards = localStorage.get(localStorageKey) || cloneDeep(defaultTempDashboard);
    // https://jira.corp.conviva.com/browse/IF-2644
    tempDashboards = tryParse(tempDashboards);
    setFromToByScrollingMark(tempDashboards);
    return Object.keys(tempDashboards.dashboards)
      .filter((key) => tempKeys.includes(`${key}`) || savedKeys.includes(key))
      .sort(
        (prevKey, currentKey) =>
          get(tempDashboards, `[${prevKey}].createTime`, 0) -
          get(tempDashboards, `[${currentKey}].createTime`, 0),
      )
      .map((key) => ({
        ...get(tempDashboards, `dashboards[${key}]`, {}),
        key,
      }));
  },
);

export const dashboardsSelector = createSelector(
  tempKeysSelector,
  savedKeysSelector,
  selectedCustomerIdSelector,
  selectedUserNameSelector,
  (tempKeys, savedKeys, customerId, userName) => {
    const localStorageKey = createKeyByUserName(prefix, customerId, userName);
    let tempDashboards = localStorage.get(localStorageKey) || cloneDeep(defaultTempDashboard);
    // https://jira.corp.conviva.com/browse/IF-2644
    tempDashboards = tryParse(tempDashboards);
    setFromToByScrollingMark(tempDashboards);
    const dashboardKeys = Object.keys(tempDashboards.dashboards).filter(
      (key) => tempKeys.includes(`${key}`) || savedKeys.includes(key),
    );

    return dashboardKeys.reduce((dashboards, key) => {
      const cloneDashboards = dashboards;
      cloneDashboards[key] = tempDashboards.dashboards[key];
      return cloneDashboards;
    }, {});
  },
);

//ECO preset scope
export const activeDashboardSelector = createSelector(
  getActiveTabId,
  currentActiveDashboard,
  dashboardListSelector,
  (activeID, currentActiveDashboard, localDashboards) => {
    let key = '';
    let dataScope = '';
    if (isAppModule) {
      if (isSavedDashboard()) {
        // saved dashboard
        const savedId = getSavedDashboardIdFromUrl();
        if (String(savedId) === String(currentActiveDashboard?.id)) {
          const { usecaseKey } = get(currentActiveDashboard, 'config', {});
          key = usecaseKey;
        }
      } else if (isPresetDashboard()) {
        // preset
        key = getPresetKeyFromUrl();
      } else if (isSharingDashboard()) {
        // sharing
        const sharingId = getSharingIdFromUrl();
        if (String(sharingId) === String(currentActiveDashboard?.id)) {
          const { usecaseKey } = get(currentActiveDashboard, 'state', {});
          key = usecaseKey;
        }
      } else if (isDrillDownDashboard()) {
        // preset drilldown
        const dirlldwonDashboard = (localDashboards as any[]).find(
          (it) => String(it.id) === activeID,
        );
        if (dirlldwonDashboard) {
          key = get(dirlldwonDashboard, 'dashboard.usecaseKey');
        }
      }

      dataScope = get(getUseCaseLimitation(key), ['dataScope'], '');
    }

    return {
      usecaseKey: key,
      dataScope,
    };
  },
);

export const hasChangedMapSelector = createSelector(dashboardsSelector, (dashboards) =>
  Object.keys(dashboards).reduce((result, key) => {
    const cloneResult = result;
    const dashboard = dashboards[key];
    cloneResult[key] = (dashboard.isSaved && dashboard.hasChanged) || !dashboard.isSaved;
    return cloneResult;
  }, {}),
);
